import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { LabelWithInfo } from '../InfoTooltip';
import { managementHelp } from '../../content/managementHelp';

export const PERIOD_PRESETS = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom' },
];

export function getPeriodPresetLabel(period) {
  return PERIOD_PRESETS.find((p) => p.value === period)?.label || period;
}

export default function PeriodFilterBar({
  period,
  customFrom,
  customTo,
  onPeriodChange,
  onCustomFromChange,
  onCustomToChange,
  onApplyCustom,
  periodLabel,
}) {
  const presetLabel = getPeriodPresetLabel(period);
  const dateRangeText = periodLabel
    || (period === 'custom' && customFrom && customTo ? `${customFrom} → ${customTo} (IST)` : '');

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
            <CalendarMonthIcon sx={{ mt: 0.5, fontSize: 32, opacity: 0.9 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.2 }}>
                  Reporting period
                </Typography>
                <LabelWithInfo
                  label=""
                  title={managementHelp.dateFilter.title}
                  body={managementHelp.dateFilter.body}
                />
              </Box>
              <Typography variant="h4" component="p" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {presetLabel}
              </Typography>
              {dateRangeText ? (
                <Typography variant="h6" component="p" sx={{ mt: 0.75, fontWeight: 500, opacity: 0.95 }}>
                  {dateRangeText}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.85 }}>
                  Select a period below to load report data.
                </Typography>
              )}
            </Box>
          </Box>
          <Chip
            label={presetLabel}
            sx={{
              bgcolor: 'rgba(255,255,255,0.16)',
              color: 'inherit',
              fontWeight: 700,
              height: 32,
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: 88 }}>
          Change period
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={period}
          exclusive
          onChange={(_, v) => v && onPeriodChange(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          {PERIOD_PRESETS.map((p) => (
            <ToggleButton key={p.value} value={p.value} sx={{ px: 1.5, textTransform: 'none', fontWeight: 600 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {period === 'custom' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', width: '100%' }}>
            <TextField
              label="From date"
              type="date"
              size="small"
              value={customFrom}
              onChange={(e) => onCustomFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To date"
              type="date"
              size="small"
              value={customTo}
              onChange={(e) => onCustomToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" size="small" onClick={onApplyCustom}>
              Apply dates
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
